import React from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames/bind'
import { ReactComponent as PlusIcon } from 'images/competitor-pricing/plus.svg'
import { ReactComponent as MinusIcon } from 'images/competitor-pricing/minus.svg'
import { PriceChangeActions } from 'components/station/components/competitor-pricing/context/constants'
import styles from './CombinedPriceControl.module.scss'

const cx = classNames.bind(styles)

/**
 * Moves both the cash and credit price of a fuel grade by one step at once, keeping the
 * gap between them fixed. Complements the individual cash/credit steppers, which change the gap.
 */
const CombinedPriceControl = ({
  dispatch,
  fuelGradeId,
  fuelGradeName,
  cashPendingRequest,
  creditPendingRequest,
}) => {
  const { t } = useTranslation(['translations'])

  const handleClick = increment => {
    // Prevent moving either price below the minimum
    if (
      !increment &&
      (cashPendingRequest?.newPrice < 1 || creditPendingRequest?.newPrice < 1)
    ) {
      return
    }

    dispatch({
      type: increment
        ? PriceChangeActions.IncrementBothPrices
        : PriceChangeActions.DecrementBothPrices,
      payload: { fuelGradeId },
    })
  }

  return (
    <div className={cx('combined-price-control')}>
      <button
        type="button"
        className={cx('button')}
        aria-label={t(
          'widgets.competitorPricing.decreaseCashAndCreditTogether'
        )}
        onClick={() => handleClick(false)}
        data-testid="combined-price-control-minus"
      >
        <MinusIcon height={16} width={16} />
      </button>
      <span className={cx('label')}>
        {t('widgets.competitorPricing.grade')} {fuelGradeName}
      </span>
      <button
        type="button"
        className={cx('button')}
        aria-label={t(
          'widgets.competitorPricing.increaseCashAndCreditTogether'
        )}
        onClick={() => handleClick(true)}
        data-testid="combined-price-control-plus"
      >
        <PlusIcon height={16} width={16} />
      </button>
    </div>
  )
}

CombinedPriceControl.propTypes = {
  dispatch: PropTypes.func.isRequired,
  fuelGradeId: PropTypes.string.isRequired,
  fuelGradeName: PropTypes.string.isRequired,
  cashPendingRequest: PropTypes.shape(),
  creditPendingRequest: PropTypes.shape(),
}

CombinedPriceControl.defaultProps = {
  cashPendingRequest: null,
  creditPendingRequest: null,
}

export { CombinedPriceControl }
